import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Loader2, TrendingUp, Users, MessageSquare, BarChart3 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function AITaskBreakdown({ taskId, onClose }) {
  const [loading, setLoading] = useState(false);
  const [breakdown, setBreakdown] = useState(null);

  const handleBreakdown = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(
        `${API}/tasks/${taskId}/breakdown`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBreakdown(response.data);
    } catch (error) {
      toast.error('Failed to generate breakdown');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent style={{ maxWidth: '600px' }}>
        <DialogHeader>
          <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Sparkles size={20} style={{ color: '#10b981' }} />
            AI Task Breakdown
          </DialogTitle>
        </DialogHeader>

        {!breakdown ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p style={{ marginBottom: '1.5rem', color: '#6b7280' }}>
              AI will analyze this task and break it down into manageable subtasks
            </p>
            <Button onClick={handleBreakdown} disabled={loading}>
              {loading ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />}
              <span style={{ marginLeft: '0.5rem' }}>Generate Breakdown</span>
            </Button>
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
                Total Estimated Time: {breakdown.total_estimated_hours} hours
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {breakdown.subtasks.map((subtask, idx) => (
                <div key={idx} style={{ background: '#f9fafb', padding: '1rem', borderRadius: '6px' }}>
                  <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
                    {idx + 1}. {subtask.title}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                    {subtask.description}
                  </div>
                  <Badge variant="secondary">{subtask.estimated_hours}h</Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function AISuggestAssignee({ taskId, onSelect }) {
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  const handleSuggest = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(
        `${API}/tasks/${taskId}/suggest-assignee`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuggestion(response.data);
    } catch (error) {
      toast.error('Failed to get suggestion');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    handleSuggest();
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '1rem' }}>
        <Loader2 className="animate-spin" size={24} style={{ margin: '0 auto' }} />
        <p style={{ marginTop: '0.5rem', color: '#6b7280' }}>AI is analyzing...</p>
      </div>
    );
  }

  if (!suggestion) return null;

  return (
    <div style={{ background: '#f0fdf4', padding: '1rem', borderRadius: '6px', border: '2px solid #10b981', marginTop: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <Sparkles size={16} style={{ color: '#10b981' }} />
        <span style={{ fontWeight: 600, color: '#10b981' }}>AI Suggestion</span>
      </div>
      <div style={{ marginBottom: '0.5rem' }}>
        <strong>{suggestion.suggested_assignee_name}</strong>
      </div>
      <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
        {suggestion.reasoning}
      </div>
      <Button 
        size="sm" 
        onClick={() => onSelect(suggestion.suggested_assignee_id)}
        style={{ background: '#10b981' }}
      >
        Assign to {suggestion.suggested_assignee_name}
      </Button>
    </div>
  );
}

export function AICommentSummary({ taskId }) {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [showSummary, setShowSummary] = useState(false);

  const handleSummarize = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(
        `${API}/tasks/${taskId}/comments/summarize`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSummary(response.data);
      setShowSummary(true);
    } catch (error) {
      toast.error('Failed to generate summary');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ marginBottom: '1rem' }}>
      <Button 
        variant="outline" 
        size="sm" 
        onClick={handleSummarize} 
        disabled={loading}
        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
      >
        {loading ? <Loader2 className="animate-spin" size={16} /> : <MessageSquare size={16} />}
        AI Summary
      </Button>

      {showSummary && summary && (
        <div style={{ marginTop: '1rem', background: '#f0fdf4', padding: '1rem', borderRadius: '6px', border: '1px solid #10b981' }}>
          <div style={{ fontWeight: 600, marginBottom: '0.5rem', color: '#10b981' }}>
            📝 AI Summary
          </div>
          <p style={{ marginBottom: '1rem' }}>{summary.summary}</p>
          
          {summary.key_points && summary.key_points.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Key Points:</div>
              <ul style={{ marginLeft: '1.5rem', fontSize: '0.875rem' }}>
                {summary.key_points.map((point, idx) => (
                  <li key={idx}>{point}</li>
                ))}
              </ul>
            </div>
          )}

          {summary.action_items && summary.action_items.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Action Items:</div>
              <ul style={{ marginLeft: '1.5rem', fontSize: '0.875rem' }}>
                {summary.action_items.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {summary.blockers && summary.blockers.length > 0 && (
            <div>
              <div style={{ fontWeight: 600, marginBottom: '0.5rem', color: '#ef4444' }}>Blockers:</div>
              <ul style={{ marginLeft: '1.5rem', fontSize: '0.875rem' }}>
                {summary.blockers.map((blocker, idx) => (
                  <li key={idx}>{blocker}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function AISentimentIndicator({ taskId }) {
  const [sentiment, setSentiment] = useState(null);

  React.useEffect(() => {
    const fetchSentiment = async () => {
      try {
        const token = localStorage.getItem('session_token');
        const response = await axios.post(
          `${API}/tasks/${taskId}/analyze-sentiment`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSentiment(response.data);
      } catch (error) {
        console.error('Failed to fetch sentiment');
      }
    };

    fetchSentiment();
  }, [taskId]);

  if (!sentiment) return null;

  const sentimentColors = {
    positive: '#10b981',
    neutral: '#6b7280',
    negative: '#ef4444',
    concerned: '#f59e0b'
  };

  const sentimentEmojis = {
    positive: '😊',
    neutral: '😐',
    negative: '😟',
    concerned: '😰'
  };

  return (
    <div style={{ 
      display: 'inline-flex', 
      alignItems: 'center', 
      gap: '0.5rem',
      padding: '0.25rem 0.75rem',
      borderRadius: '9999px',
      background: `${sentimentColors[sentiment.sentiment]}20`,
      border: `1px solid ${sentimentColors[sentiment.sentiment]}`,
      fontSize: '0.875rem'
    }}>
      <span>{sentimentEmojis[sentiment.sentiment]}</span>
      <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>
        {sentiment.sentiment}
      </span>
    </div>
  );
}

export function AITaskPrediction({ taskId }) {
  const [prediction, setPrediction] = useState(null);

  React.useEffect(() => {
    const fetchPrediction = async () => {
      try {
        const token = localStorage.getItem('session_token');
        const response = await axios.post(
          `${API}/tasks/${taskId}/predict-completion`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setPrediction(response.data);
      } catch (error) {
        console.error('Failed to fetch prediction');
      }
    };

    fetchPrediction();
  }, [taskId]);

  if (!prediction) return null;

  return (
    <div style={{ background: '#eff6ff', padding: '1rem', borderRadius: '6px', marginTop: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <TrendingUp size={16} style={{ color: '#3b82f6' }} />
        <span style={{ fontWeight: 600 }}>AI Prediction</span>
      </div>
      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
        Estimated completion: <strong>{prediction.predicted_date}</strong>
      </div>
      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
        Confidence: {prediction.confidence}
      </div>
    </div>
  );
}

export function AIStatusUpdate({ taskId }) {
  const [loading, setLoading] = useState(false);
  const [update, setUpdate] = useState(null);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(
        `${API}/tasks/${taskId}/generate-update`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUpdate(response.data);
    } catch (error) {
      toast.error('Failed to generate update');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ marginTop: '1rem' }}>
      <Button 
        variant="outline" 
        size="sm" 
        onClick={handleGenerate} 
        disabled={loading}
        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
      >
        {loading ? <Loader2 className="animate-spin" size={16} /> : <BarChart3 size={16} />}
        Generate Status Update
      </Button>

      {update && (
        <div style={{ marginTop: '1rem', background: '#f0fdf4', padding: '1rem', borderRadius: '6px' }}>
          <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>AI-Generated Update:</div>
          <p style={{ marginBottom: '1rem' }}>{update.update}</p>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            Progress: {update.progress_percentage}%
          </div>
          {update.next_steps && update.next_steps.length > 0 && (
            <div style={{ marginTop: '0.5rem' }}>
              <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.25rem' }}>Next Steps:</div>
              <ul style={{ marginLeft: '1.5rem', fontSize: '0.875rem' }}>
                {update.next_steps.map((step, idx) => (
                  <li key={idx}>{step}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
